import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const getServiceIcon = (name) => {
  const nameLower = (name || '').toLowerCase();
  if (nameLower.includes('débouchage') || nameLower.includes('debouchage')) return 'Wrench';
  if (nameLower.includes('fuite') || nameLower.includes('réparation')) return 'Droplet';
  if (nameLower.includes('robinet') || nameLower.includes('remplacement')) return 'Settings';
  if (nameLower.includes('diagnostic') || nameLower.includes('inspection')) return 'Search';
  if (nameLower.includes('installation') || nameLower.includes('équipement')) return 'Package';
  if (nameLower.includes('urgence') || nameLower.includes('dépannage')) return 'AlertCircle';
  return 'Wrench';
};

const buildServiceFeatures = (service) => {
  const features = [];
  if (service?.Guarantee) features.push(`Garantie ${service.Guarantee} mois`);
  return features.length > 0 ? features : ['Service professionnel'];
};

const transformServiceFromApi = (raw) => {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name || 'Sans nom',
    description: raw.description || '',
    reasoning: raw.reasoning,
    confidence: raw.confidence,
    estimatedDuration: raw.estimatedDuration,
    Guarantee: raw.Guarantee,
    TVA: raw.TVA,
    requiredSkills: raw.requiredSkills,
    clientValidation: raw.clientValidation,
    checklists: raw.checklists,
    icon: getServiceIcon(raw.name),
    features: buildServiceFeatures(raw),
  };
};

const PromptStep = ({ data, onUpdate, onNext, onBack, onSkipToWizard }) => {
  const [description, setDescription] = useState(data?.promptDescription || '');
  const [loading, setLoading] = useState(false);
  const [suggestedService, setSuggestedService] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const handleFindService = async () => {
    const text = description?.trim();
    if (!text) {
      setError('Veuillez décrire votre problème en quelques mots.');
      return;
    }
    setError('');
    setLoading(true);
    setSuggestedService(null);
    setNotFound(false);
    try {
      const { data: result, error: invokeError } = await supabase?.functions?.invoke('suggest-service', {
        body: { description: text },
      });
      if (invokeError) throw invokeError;
      if (result?.error) {
        setNotFound(true);
        setSuggestedService(null);
        return;
      }
      if (result?.serviceId && result?.service) {
        setSuggestedService(transformServiceFromApi(result.service));
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.error('suggest-service error:', err);
      setError(err?.message || 'Impossible de trouver une prestation. Utilisez le parcours étape par étape.');
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmService = () => {
    if (!suggestedService) return;
    onUpdate({
      service: suggestedService,
      promptDescription: description?.trim(),
    });
  };

  const handleRefuseAndGoWizard = () => {
    setSuggestedService(null);
    setNotFound(false);
    if (typeof onSkipToWizard === 'function') {
      onSkipToWizard();
    }
  };

  const showWizardChoice = notFound || suggestedService;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="MessageCircle" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Décrivez votre problème</h2>
            <p className="text-muted-foreground">
              En quelques mots, décrivez ce dont vous avez besoin. Nous vous proposerons la prestation adaptée, ou vous pourrez la choisir étape par étape.
            </p>
          </div>
        </div>

        {!showWizardChoice ? (
          <>
            <div className="space-y-4">
              {/* <label className="block text-sm font-medium text-foreground">
                Votre problème (en français)
              </label> */}
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setError('');
                }}
                placeholder="Ex : mon évier est bouché, fuite sous le lavabo, robinet qui goutte, chauffe-eau en panne..."
                className="w-full px-4 py-3 border border-border rounded-lg bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px] resize-y"
                rows={4}
                disabled={loading}
              />
              {error && (
                <p className="text-sm text-error flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} />
                  {error}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-8 mt-8 border-t border-border">
              {onBack && (
                <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
                  Retour
                </Button>
              )}
              <Button
                variant="default"
                size="lg"
                iconName="Search"
                iconPosition="right"
                onClick={handleFindService}
                disabled={loading || !description?.trim()}
              >
                {loading ? 'Recherche en cours...' : 'Trouver ma prestation'}
              </Button>
              <button
                type="button"
                onClick={handleRefuseAndGoWizard}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Choisir ma prestation étape par étape
              </button>
            </div>
          </>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
            <p className="text-muted-foreground">Recherche d&apos;une prestation adaptée...</p>
          </div>
        ) : suggestedService ? (
          <div className="space-y-6">
            <p className="text-foreground font-medium">Voici la prestation qui nous semble correspondre :</p>
            <div className="p-6 rounded-xl border-2 border-primary bg-primary/5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Icon name={suggestedService.icon} size={20} color="var(--color-primary)" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{suggestedService.name}</h3>
              </div>
              {suggestedService.description && (
                <p className="text-sm text-muted-foreground">{suggestedService.description}</p>
              )}
              {suggestedService.reasoning && (
                <p className="text-xs text-muted-foreground italic">Pourquoi cette prestation : {suggestedService.reasoning}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="default" size="lg" iconName="CheckCircle" onClick={handleConfirmService}>
                C&apos;est ma prestation
              </Button>
              <Button variant="outline" size="lg" onClick={handleRefuseAndGoWizard}>
                Ce n&apos;est pas ça, choisir étape par étape
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Nous n&apos;avons pas pu identifier une solution répondant à votre besoin.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Veuillez utiliser le mode de diagnostic guidé .
                </p>
              </div>
            </div>
            <Button variant="default" size="lg" iconName="ArrowRight" iconPosition="right" onClick={handleRefuseAndGoWizard}>
              Choisir ma prestation étape par étape
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptStep;
